import React from 'react';
import {
    User,
    Phone,
    Building2,
    FileText,
    Calendar,
} from 'lucide-react';
import type { Customer } from '../../types';

export interface WorkflowInfoTabProps {
    customer: Customer;
}

const WorkflowInfoTab: React.FC<WorkflowInfoTabProps> = ({ customer }) => {
    return (
        <div className="bg-white rounded-lg shadow">
            <div className="divide-y divide-gray-200">
                {/* Personal Information Section */}
                <div className="px-6 py-5">
                    <div className="flex items-center mb-5">
                        <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                            <User className="w-4 h-4 text-gray-600" />
                        </div>
                        <h3 className="text-base font-semibold text-gray-900">Informations personnelles</h3>
                    </div>
                    <dl className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-5">
                        <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Nom complet</dt>
                            <dd className="text-sm text-gray-900 font-medium">{customer.full_name || 'Non renseign\u00e9'}</dd>
                        </div>
                        <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Sexe</dt>
                            <dd className="text-sm text-gray-900">{customer.sexe || 'Non renseign\u00e9'}</dd>
                        </div>
                        <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Date de naissance</dt>
                            <dd className="text-sm text-gray-900">{customer.dat || 'Non renseign\u00e9'}</dd>
                        </div>
                        <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">N° AVS</dt>
                            <dd className="text-sm text-gray-900 font-mono">{customer.avs || 'Non renseign\u00e9'}</dd>
                        </div>
                        <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">État civil</dt>
                            <dd className="text-sm text-gray-900">{customer.civil || 'Non renseign\u00e9'}</dd>
                        </div>
                        {customer.date_civil && (
                            <div>
                                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Date état civil</dt>
                                <dd className="text-sm text-gray-900">{customer.date_civil}</dd>
                            </div>
                        )}
                    </dl>
                </div>

                {/* Contact Information Section */}
                <div className="px-6 py-5">
                    <div className="flex items-center mb-5">
                        <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                            <Phone className="w-4 h-4 text-gray-600" />
                        </div>
                        <h3 className="text-base font-semibold text-gray-900">Coordonnées</h3>
                    </div>
                    <dl className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-5">
                        <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Email / Login</dt>
                            <dd className="text-sm text-gray-900">{customer.login || 'Non renseign\u00e9'}</dd>
                        </div>
                        <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Téléphone</dt>
                            <dd className="text-sm text-gray-900">{customer.tel || 'Non renseign\u00e9'}</dd>
                        </div>
                        <div className="md:col-span-1">
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Pays</dt>
                            <dd className="text-sm text-gray-900">{customer.pays || 'Non renseign\u00e9'}</dd>
                        </div>
                        <div className="md:col-span-3">
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Adresse complète</dt>
                            <dd className="text-sm text-gray-900">
                                {customer.adr && customer.npa && customer.loc
                                    ? `${customer.adr}, ${customer.npa} ${customer.loc}`
                                    : customer.adr || 'Non renseign\u00e9'}
                            </dd>
                        </div>
                    </dl>
                </div>

                {/* Professional Information Section */}
                <div className="px-6 py-5">
                    <div className="flex items-center mb-5">
                        <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                            <Building2 className="w-4 h-4 text-gray-600" />
                        </div>
                        <h3 className="text-base font-semibold text-gray-900">Informations professionnelles</h3>
                    </div>
                    <dl className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-5">
                        <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Situation professionnelle</dt>
                            <dd className="text-sm text-gray-900">{customer.sit_pro || 'Non renseign\u00e9'}</dd>
                        </div>
                        <div className="md:col-span-2">
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Profession</dt>
                            <dd className="text-sm text-gray-900">{customer.profession || 'Non renseign\u00e9'}</dd>
                        </div>
                    </dl>
                </div>

                {/* LPP Information Section */}
                <div className="px-6 py-5">
                    <div className="flex items-center mb-5">
                        <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                            <FileText className="w-4 h-4 text-gray-600" />
                        </div>
                        <h3 className="text-base font-semibold text-gray-900">Informations LPP</h3>
                    </div>
                    <dl className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-5">
                        <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Souhait</dt>
                            <dd className="text-sm text-gray-900">{customer.souhait || 'Non renseign\u00e9'}</dd>
                        </div>
                        <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Fondation</dt>
                            <dd className="text-sm text-gray-900">{customer.fondation || 'Non renseign\u00e9'}</dd>
                        </div>
                        <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Onboarding</dt>
                            <dd className="text-sm text-gray-900">{customer.onboarding || 'Non'}</dd>
                        </div>
                        <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">État</dt>
                            <dd className="text-sm text-gray-900">{customer.etat || 'Non renseign\u00e9'}</dd>
                        </div>
                        <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Task ID</dt>
                            <dd className="text-sm text-gray-900 font-mono">{customer.taskId || 'Non renseign\u00e9'}</dd>
                        </div>
                        <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Client ID</dt>
                            <dd className="text-sm text-gray-900 font-mono">{customer.clientId || 'Non renseign\u00e9'}</dd>
                        </div>
                        {customer.montantSuppletive && parseFloat(customer.montantSuppletive.toString()) > 0 && (
                            <div>
                                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Montant supplétive</dt>
                                <dd className="text-sm text-gray-900 font-semibold">CHF {parseFloat(customer.montantSuppletive.toString()).toLocaleString('fr-CH')}</dd>
                            </div>
                        )}
                        {customer.catSuppletive && (
                            <div>
                                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Catégorie supplétive</dt>
                                <dd className="text-sm text-gray-900">{customer.catSuppletive}</dd>
                            </div>
                        )}
                    </dl>
                </div>

                {/* Administrative Information Section */}
                <div className="px-6 py-5 bg-gray-50">
                    <div className="flex items-center mb-5">
                        <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center mr-3">
                            <Calendar className="w-4 h-4 text-gray-600" />
                        </div>
                        <h3 className="text-base font-semibold text-gray-900">Informations système</h3>
                    </div>
                    <dl className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-5">
                        <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">UUID</dt>
                            <dd className="text-sm text-gray-900 font-mono text-xs">{customer.uuid || 'Non renseign\u00e9'}</dd>
                        </div>
                        <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Date de création</dt>
                            <dd className="text-sm text-gray-900">
                                {customer.createdAt ? new Date(customer.createdAt).toLocaleDateString('fr-CH', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                }) : 'Non renseign\u00e9'}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Compagnie</dt>
                            <dd className="text-sm text-gray-900">{customer.company_name || customer.company_id || 'Non renseign\u00e9'}</dd>
                        </div>
                        {customer.agent && (
                            <div>
                                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Agent</dt>
                                <dd className="text-sm text-gray-900">{customer.agent}</dd>
                            </div>
                        )}
                    </dl>
                </div>
            </div>
        </div>
    );
};

export default WorkflowInfoTab;
